import { createRouter, createWebHistory } from 'vue-router'
import { agentGuard, agentGuestGuard } from '@/features/auth/guards/auth.guard'
import { useAuthStore } from '@/features/auth/stores/auth.store'
import { isImpersonating, isImpersonationExpired, tokenStorage } from '@/features/auth/tokenStorage'
import { isRoleAllowedInPortal } from '@/features/auth/types/auth.types'
import AgentLayout from '@/layouts/AgentLayout.vue'

import { sectionGuard } from './guards/section.guard'

import './route-meta'

// Отдельное агентское приложение (agents.vinprop.vn): только кабинет агента.
// Внутренняя CRM (/dashboard/*) сюда не входит — не попадает в бандл и не грузится агентом.
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Вкладка просмотра «глазами агента» (DEV-2400, ADR-0064). Вне AgentLayout:
    // каркасу кабинета здесь показывать нечего, а его запрос профиля ушёл бы без
    // сеанса. skipAuthInit — гард не поднимает тут сеанс из localStorage.
    {
      path: '/impersonate',
      name: 'impersonate',
      component: () => import('@/features/auth/views/ImpersonateView.vue'),
      meta: { requiresAuth: false, skipAuthInit: true },
    },
    {
      path: '/impersonation-ended',
      name: 'impersonation-ended',
      component: () => import('@/features/auth/views/ImpersonationEndedView.vue'),
      meta: { requiresAuth: false, skipAuthInit: true },
    },
    // Отдельный хост agents.vinprop.vn — кабинет живёт в корне (без префикса /agents).
    {
      path: '/',
      component: AgentLayout,
      children: [
        { path: '', redirect: '/dashboard' },
        {
          path: 'login',
          name: 'agent-login',
          component: () => import('@/features/agent/views/AgentLoginView.vue'),
          beforeEnter: agentGuestGuard,
          meta: { requiresAuth: false },
        },
        {
          path: 'register',
          name: 'agent-register',
          component: () => import('@/features/agent/views/AgentRegisterView.vue'),
          beforeEnter: agentGuestGuard,
          meta: { requiresAuth: false },
        },
        {
          // Публичная оферта (DEV-365). Без guard — читается и до регистрации,
          // и авторизованным агентом из кабинета.
          path: 'offer',
          name: 'agent-offer',
          component: () => import('@/features/agent/views/AgentOfferView.vue'),
          meta: { requiresAuth: false },
        },
        {
          path: 'forgot-password',
          name: 'agent-forgot-password',
          component: () => import('@/features/agent/views/AgentForgotPasswordView.vue'),
          beforeEnter: agentGuestGuard,
          meta: { requiresAuth: false },
        },
        {
          // URL из письма auth (DEV-229/DEV-1014): {agentBase}/reset-password/{token}
          path: 'reset-password/:token',
          name: 'agent-reset-password',
          component: () => import('@/features/agent/views/AgentResetPasswordView.vue'),
          meta: { requiresAuth: false },
        },
        {
          // URL из письма auth (DEV-1308): {agentBase}/accept-invite/{token}.
          // Сюда ведут приглашения ролей, открывающих только кабинет агента.
          //
          // Без agentGuestGuard намеренно: письмо открывают и с залогиненной
          // вкладки, и выкидывать человека на дашборд вместо приглашения было
          // бы тупиком.
          path: 'accept-invite/:token',
          name: 'agent-accept-invite',
          component: () => import('@/features/agent/views/AgentAcceptInviteView.vue'),
          meta: { requiresAuth: false },
        },
        {
          // URL из письма auth (DEV-229/DEV-1014): {agentBase}/confirm-email/{token}
          path: 'confirm-email/:token',
          name: 'agent-confirm-email',
          component: () => import('@/features/agent/views/AgentConfirmEmailView.vue'),
          meta: { requiresAuth: false },
        },
        {
          path: 'home',
          name: 'agent-home',
          component: () => import('@/features/agent/views/AgentHomeView.vue'),
          beforeEnter: agentGuard,
          meta: { requiresAuth: true, section: 'agent_home' },
        },
        // Дашборд кабинета v2 (DEV-490) — основной экран после входа.
        {
          path: 'dashboard',
          name: 'agent-dashboard',
          component: () => import('@/features/agent/views/AgentDashboardView.vue'),
          beforeEnter: agentGuard,
          meta: { requiresAuth: true, section: 'agent_dashboard' },
        },
        // Мои клиенты (DEV-491, C2).
        {
          path: 'clients',
          name: 'agent-clients',
          component: () => import('@/features/agent/views/AgentClientsView.vue'),
          beforeEnter: agentGuard,
          meta: { requiresAuth: true, section: 'agent_clients' },
        },
        // Вознаграждение (DEV-492, C6).
        {
          path: 'reward',
          name: 'agent-reward',
          component: () => import('@/features/agent/views/AgentRewardView.vue'),
          beforeEnter: agentGuard,
          meta: { requiresAuth: true, section: 'agent_reward' },
        },
        // Обучение (DEV-495, C8).
        {
          path: 'education',
          name: 'agent-education',
          component: () => import('@/features/agent/views/AgentEducationView.vue'),
          beforeEnter: agentGuard,
          meta: { requiresAuth: true, section: 'agent_education' },
        },
        // Профиль и компания (DEV-494, C7).
        {
          path: 'profile',
          name: 'agent-profile',
          component: () => import('@/features/agent/views/AgentProfileView.vue'),
          beforeEnter: agentGuard,
          meta: { requiresAuth: true, section: 'agent_profile' },
        },
        // Проекты — доступные агенту ЖК (DEV-1003, C11/C12).
        {
          path: 'projects',
          name: 'agent-projects',
          component: () => import('@/features/agent/views/AgentProjectsView.vue'),
          beforeEnter: agentGuard,
          meta: { requiresAuth: true, section: 'agent_projects' },
        },
        {
          path: 'projects/:id',
          name: 'agent-project-detail',
          component: () => import('@/features/agent/views/AgentProjectDetailView.vue'),
          beforeEnter: agentGuard,
          meta: { requiresAuth: true, section: 'agent_projects' },
        },
        // Презентации — обезличенные офферы по ЖК (DEV-1015, C13/C14/C15).
        {
          path: 'presentations',
          name: 'agent-presentations',
          component: () => import('@/features/presentations/views/AgentPresentationsView.vue'),
          beforeEnter: agentGuard,
          meta: { requiresAuth: true, section: 'agent_presentations' },
        },
        {
          path: 'presentations/new',
          name: 'agent-presentation-new',
          component: () =>
            import('@/features/presentations/views/AgentPresentationBuilderView.vue'),
          beforeEnter: agentGuard,
          meta: { requiresAuth: true, section: 'agent_presentations' },
        },
        {
          path: 'presentations/:id',
          name: 'agent-presentation-detail',
          component: () => import('@/features/presentations/views/AgentPresentationDetailView.vue'),
          beforeEnter: agentGuard,
          meta: { requiresAuth: true, section: 'agent_presentations' },
        },
        // Поддержка (DEV-496, C9).
        {
          path: 'support',
          name: 'agent-support',
          component: () => import('@/features/agent/views/AgentSupportView.vue'),
          beforeEnter: agentGuard,
          meta: { requiresAuth: true, section: 'agent_support' },
        },
        {
          path: 'submit-client',
          name: 'agent-submit-client',
          component: () => import('@/features/agent/views/AgentSubmitClientView.vue'),
          beforeEnter: agentGuard,
          meta: { requiresAuth: true, section: 'agent_submit_client' },
        },
        // Мои сделки (DEV-359, D8/D9). Служит и селектором сделки для сценариев
        // вознаграждения/встречи (DEV-360/361): заход с ?next=commission|meeting.
        {
          path: 'deals',
          name: 'agent-deals',
          component: () => import('@/features/agent/views/AgentMyDealsView.vue'),
          beforeEnter: agentGuard,
          meta: { requiresAuth: true, section: 'agent_deals' },
        },
        {
          path: 'partnership',
          name: 'agent-partnership',
          component: () => import('@/features/agent/views/AgentPartnershipView.vue'),
          beforeEnter: agentGuard,
          meta: { requiresAuth: true, section: 'agent_partnership' },
        },
        {
          path: 'commission',
          name: 'agent-commission',
          component: () => import('@/features/agent/views/AgentCommissionView.vue'),
          beforeEnter: agentGuard,
          meta: { requiresAuth: true, section: 'agent_commission' },
        },
        {
          path: 'meeting',
          name: 'agent-meeting',
          component: () => import('@/features/agent/views/AgentMeetingView.vue'),
          beforeEnter: agentGuard,
          meta: { requiresAuth: true, section: 'agent_meeting' },
        },
        {
          // DEV-1769: экран просмотра одного ролика. Без slug смотреть нечего —
          // отправляем в каталог на /education.
          path: 'strategies/:slug?',
          name: 'agent-strategies',
          component: () => import('@/features/agent/views/AgentStrategiesView.vue'),
          beforeEnter: (to, _from, next) => {
            if (!to.params.slug) {
              next({ name: 'agent-education' })
              return
            }

            agentGuard(to, _from, next)
          },
          meta: { requiresAuth: true, section: 'agent_strategies' },
        },
        {
          path: 'other',
          name: 'agent-other',
          component: () => import('@/features/agent/views/AgentOtherQuestionView.vue'),
          beforeEnter: agentGuard,
          meta: { requiresAuth: true, section: 'agent_other' },
        },
        // «Мои обращения» агента (DEV-438): список + тред с ответом.
        {
          path: 'inquiries',
          name: 'agent-inquiries',
          component: () => import('@/features/agent/views/AgentMyInquiriesView.vue'),
          beforeEnter: agentGuard,
          meta: { requiresAuth: true, section: 'agent_inquiries' },
        },
        {
          path: 'inquiries/:id',
          name: 'agent-inquiry-detail',
          component: () => import('@/features/agent/views/AgentInquiryThreadView.vue'),
          beforeEnter: agentGuard,
          meta: { requiresAuth: true, section: 'agent_inquiries' },
        },
      ],
    },
    // Экран отказа живёт ВНЕ AgentLayout: карта пуста, меню рисовать нечем.
    // requiresAuth здесь false намеренно — гейтить его картой значило бы
    // зациклить редирект на самого себя (DEV-1265).
    {
      path: '/no-access',
      name: 'agent-no-access',
      component: () => import('@/features/agent/views/AgentNoAccessView.vue'),
      meta: { requiresAuth: false },
    },
    // Любой неизвестный путь → дашборд (guard уведёт на /login, если не авторизован).
    // Также подхватывает старые ссылки вида /agents/* после выпила префикса.
    { path: '/:pathMatch(.*)*', redirect: '/dashboard' },
  ],
})

// Инициализация auth один раз на старте, если в localStorage есть токены.
let isInitialized = false

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Сеанс просмотра истёк, пока вкладка спала или была закрыта: сервер его уже
  // не примет, поднимать сеанс ради 401 незачем (DEV-2400).
  if (isImpersonationExpired() && !to.meta.skipAuthInit) {
    next({ name: 'impersonation-ended' })
    return
  }

  if (!isInitialized && !to.meta.skipAuthInit) {
    // У вкладки просмотра refresh-токена нет по устройству.
    const hasTokens =
      tokenStorage.getAccessToken() && (tokenStorage.getRefreshToken() || isImpersonating())
    if (hasTokens) {
      await authStore.initialize()
    }
    isInitialized = true
  }

  // DEV-1167/DEV-1177. Партнёр — внешний интегратор: заводится тем же auth,
  // логинится той же формой и до этой проверки проваливался в кабинет.
  // Гасим сессию целиком, а не редиректим: живой токен в localStorage создаёт
  // видимость входа, а пускать партнёра сюда мы не будем ни на одном маршруте.
  //
  // ⚠️ DEV-1265: страховка остаётся до отдельного решения. Список пускает ВСЕХ
  // сотрудников (ceo, admin, hos, manager, support_manager, marketer,
  // project_manager) — он повторял то, что пускалось до появления гварда, — а
  // карта разделов даёт непустой ответ только роли AGENT. Снять его здесь
  // значит закрыть кабинет всем сотрудникам разом; делать это вслепую нельзя.
  if (authStore.isAuthenticated && !isRoleAllowedInPortal(authStore.user?.roles)) {
    await authStore.logout({ redirect: false })
    next({ path: '/login', query: { reason: 'role_not_allowed' } })
    return
  }

  await sectionGuard(to, from, next)
})

export default router
